/**
 * Upload step — runs Google's validate_only as a final preflight, then
 * triggers a `ConfirmDialog` before the real ingest.
 *
 * Per CLAUDE.md Theme #4: never a native confirm(). Copy is adapted based on
 * whether every row has a stable transaction_id (Google's dedup key) — when
 * present, resubmits after a failure are safe.
 */
import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { offlineConversionsApi } from '@/services/api/offlineConversions';
import type {
  Consent,
  OfflineConversionEvent,
  UploadEnhancedLeadsRequest,
  UploadEnhancedLeadsResponse,
} from '@/services/api/offlineConversions';

interface Props {
  googleAdsAccountId: string;
  productDestinationId: string;
  events: OfflineConversionEvent[];
  consent: Consent;
  onSubmitted: (requestId: string) => void;
}

function isApiErrorDetail(value: unknown): value is { code?: string; message?: string } {
  return typeof value === 'object' && value !== null;
}

export const UploadStep: React.FC<Props> = ({
  googleAdsAccountId,
  productDestinationId,
  events,
  consent,
  onSubmitted,
}) => {
  const [validateMsg, setValidateMsg] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const request: UploadEnhancedLeadsRequest = {
    google_ads_account_id: googleAdsAccountId,
    product_destination_id: productDestinationId,
    encoding: 'HEX',
    consent,
    events,
  };

  const validate = useMutation<UploadEnhancedLeadsResponse, Error>({
    mutationFn: () => offlineConversionsApi.validate({ ...request, validate_only: true }),
    onSuccess: () => setValidateMsg('Google accepted the request shape.'),
    onError: (err) => {
      setValidateMsg(humanizeError(err));
    },
  });

  const upload = useMutation<UploadEnhancedLeadsResponse, Error>({
    mutationFn: async () => {
      const res = await offlineConversionsApi.upload(request);
      // A real (non-validate) upload must come back with a request_id —
      // the /status poll authorization keys off it, and the wizard's next
      // step can't function without it. Treat a missing ID as a hard
      // error rather than silently leaving the wizard stuck on this step.
      if (!res.request_id) {
        throw new Error(
          'Upload accepted by the backend but no request_id was returned. ' +
          'Diagnostics polling cannot proceed; contact support.',
        );
      }
      return res;
    },
    onSuccess: (res) => {
      // Non-null-assertion safe — the mutationFn throws on missing id.
      onSubmitted(res.request_id!);
    },
  });

  const allRowsHaveTxn = events.every((e) => Boolean(e.transaction_id));
  const confirmMessage = allRowsHaveTxn
    ? `Upload ${events.length} conversion${events.length === 1 ? '' : 's'} to Google Ads account ${googleAdsAccountId}. Google will match these against ad clicks asynchronously — diagnostics resolve within a few hours; attributed conversions appear in reporting within 24–48 hours. Each row has a stable transaction ID, so resubmitting after a failure won't double-count.`
    : `Upload ${events.length} conversion${events.length === 1 ? '' : 's'} to Google Ads account ${googleAdsAccountId}. ⚠ Some rows lack a stable transaction ID — if this upload fails and you resubmit, surviving rows may be counted twice.`;

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-700">
        Ready to send <strong>{events.length}</strong> row{events.length === 1 ? '' : 's'}.
      </p>

      <div className="flex gap-2">
        <Button
          variant="secondary"
          onClick={() => validate.mutate()}
          disabled={validate.isPending || events.length === 0}
        >
          {validate.isPending ? 'Validating…' : 'Validate with Google (no upload)'}
        </Button>
        <Button
          variant="primary"
          onClick={() => setConfirmOpen(true)}
          disabled={upload.isPending || events.length === 0}
        >
          Upload
        </Button>
      </div>

      {validateMsg && (
        <Alert variant={validate.isError ? 'warning' : 'info'}>{validateMsg}</Alert>
      )}
      {upload.isError && (
        <Alert variant="error">{humanizeError(upload.error)}</Alert>
      )}

      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          upload.mutate();
        }}
        title="Send offline conversions to Google?"
        message={confirmMessage}
        confirmText="Upload"
        cancelText="Cancel"
        variant="warning"
        isLoading={upload.isPending}
      />
    </div>
  );
};

function humanizeError(err: unknown): string {
  if (!err) return 'Unknown error.';
  // Axios shapes — response.data.detail can be a string or a structured object
  // depending on whether we hit one of the ECL-specific detail codes.
  const maybeAxios = err as { response?: { data?: { detail?: unknown } }; message?: string };
  const detail = maybeAxios.response?.data?.detail;
  if (typeof detail === 'string') {
    if (detail === 'customer_data_terms_not_accepted') {
      return 'Customer Data Terms have not been accepted in Google Ads. Open Goals → Settings → Customer data terms.';
    }
    if (detail === 'enhanced_conversions_for_leads_not_enabled') {
      return 'Enhanced conversions for leads is turned off. Enable it at Goals → Settings.';
    }
    return detail;
  }
  if (isApiErrorDetail(detail)) {
    return detail.message ?? JSON.stringify(detail);
  }
  return maybeAxios.message ?? 'Upload failed.';
}
